import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  Activity, 
  Smartphone, 
  MapPin, 
  AlertTriangle, 
  TrendingUp, 
  Zap,
  Clock,
  File,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-toastify';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const { get } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  // Get admin ID from user object
  const getAdminId = () => {
    return user?.id || user?._id || user?.admin_id || user?.userId || user?.user_id || null;
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const dateParam = getDateParam();
      const adminId = getAdminId();
      
      // Build query parameters
      const params = new URLSearchParams({
        date: dateParam
      });
      
      // Add admin_id if available
      if (adminId) {
        params.append('admin_id', adminId);
      }
      
      const response = await get(`/admin/dashboard?${params.toString()}`);
      setDashboardData(response);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      setIsLoading(false);
    }
  };

  const periodOptions = [
    { value: 'today', label: 'Today', apiParam: 'today' },
    { value: 'lastWeek', label: 'Last Week', apiParam: 'last_week' },
    { value: 'thisWeek', label: 'This Week', apiParam: 'this_week' },
    { value: 'lastMonth', label: 'Last Month', apiParam: 'last_month' },
    { value: 'thisMonth', label: 'This Month', apiParam: 'this_month' }
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    fetchDashboardData();
  };

  // Map period to API date parameter
  const getDateParam = () => {
    const selectedOption = periodOptions.find(opt => opt.value === selectedPeriod);
    return selectedOption?.apiParam || 'today';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Get alert type color
  const getAlertTypeColor = (alertType) => {
    switch (alertType) {
      case 'SOS Alert':
        return 'bg-red-500';
      case 'Ignition ON':
        return 'bg-green-500';
      case 'Ignition OFF':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  // Summary cards configuration for admin (no Total Accounts)
  const getSummaryCards = () => {
    if (!dashboardData?.summary) return [];
    
    const { summary } = dashboardData;
    return [
      { 
        title: 'Total Assinged Devices', 
        value: summary.total_devices || 0, 
        icon: Smartphone,
        color: 'bg-blue-500',
        bgGradient: 'from-blue-500 to-blue-600',
        description: 'Assinged Devices',
        path: '/admin/device-assignments/all'
      },
      { 
        title: 'Active Devices', 
        value: summary.total_trips || 0, 
        icon: MapPin,
        color: 'bg-green-500',
        bgGradient: 'from-green-500 to-green-600',
        description: 'Active devices',
        path: '/admin/map'
      },
      { 
        title: 'Total Quotes', 
        value: summary.total_quotes || 0, 
        icon: File,
        color: 'bg-yellow-500',
        bgGradient: 'from-yellow-500 to-yellow-600',
        description: 'Total Quotes',
        path: '/admin/quotes'
      },
      { 
        title: 'Recharge History', 
        value: summary.total_recharge_history || 0, 
        icon: CreditCard,
        color: 'bg-purple-500',
        bgGradient: 'from-purple-500 to-purple-600',
        description: 'Recharge history',
        path: '/admin/recharge'
      }
    ];
  };

  // Trip Trends Chart Data
  const getTripTrendsChartData = () => {
    if (!dashboardData?.trip_trends || dashboardData.trip_trends.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'Trips',
          data: [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
        }]
      };
    }

    const trends = dashboardData.trip_trends;
    return {
      labels: trends.map(t => formatDate(t.date)),
      datasets: [{
        label: 'Trips',
        data: trends.map(t => t.total),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }]
    };
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          stepSize: 1,
        }
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const summaryCards = getSummaryCards();
  const tripTrendsData = getTripTrendsChartData();

  return (
    <div className="p-6 space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-sm text-muted-foreground">Overview of your system performance</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[200px] h-11 rounded-xl text-muted-foreground border-1 border-muted-foreground/20">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent className='border-1 border-muted-foreground/20'>
              {periodOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-muted/70 rounded-xl shadow-sm animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {summaryCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card 
                key={index} 
                className="group relative overflow-hidden border-1 border-muted-foreground/20 bg-gradient-to-br from-background to-muted/30 hover:shadow-lg transition-all duration-300 animate-in slide-in-from-bottom-4 cursor-pointer hover:scale-105"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => card.path && navigate(card.path)}
              >
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${card.bgGradient} opacity-10 rounded-full blur-2xl`}></div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground mb-2">{card.title}</p>
                      <h3 className="text-xl font-bold text-foreground mb-1">{card.value}</h3>
                      <p className="text-xs text-muted-foreground">{card.description}</p>
                    </div>
                    <div className={`p-2 rounded-xl bg-gradient-to-br ${card.bgGradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Trip Trends and Alerts Section - Same Row for Admin */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trip Trends Chart */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  Trip Trends
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Daily trip count over time
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-64 bg-muted/50 rounded-lg animate-pulse"></div>
            ) : (
              <div className="h-64">
                <Line data={tripTrendsData} options={lineChartOptions} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="border-1 border-muted-foreground/20 bg-muted/30 hover:shadow-lg transition-shadow duration-300">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Recent Alerts
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Latest system alerts and notifications
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-16 bg-muted/50 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : dashboardData?.recent_alerts && dashboardData.recent_alerts.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {dashboardData.recent_alerts.map((alert, index) => (
                  <div 
                    key={alert.id} 
                    className="p-4 rounded-lg border-1 border-muted-foreground/20 bg-background/50 hover:bg-background hover:shadow-md transition-all duration-200 animate-in slide-in-from-left-4"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${getAlertTypeColor(alert.alert_type)} text-white`}>
                          <AlertTriangle className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-sm text-foreground">{alert.alert_type}</h4>
                            {alert.raw_code && (
                              <Badge variant="outline" className="text-xs">
                                {alert.raw_code}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            Terminal: {alert.terminal_id}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            {alert.voltage !== undefined && (
                              <div className="flex items-center gap-1">
                                <Zap className="h-3 w-3" />
                                <span>{alert.voltage}V</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{formatDateTime(alert.created_at || alert.timestamp)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">No alerts found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Additional CSS for animations */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
          background-size: 200% 100%;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;

